import { useEffect, useState, useCallback } from "react";
import { Toaster, toast } from "sonner";
import { motion } from "framer-motion";
import {
  LogOut,
  RefreshCw,
  Phone,
  Mail,
  MapPin,
  Loader2,
  Inbox,
  Store,
  CalendarCheck,
} from "lucide-react";
import {
  adminLogin,
  fetchMe,
  fetchLeads,
  fetchStats,
  setToken,
  clearToken,
  getToken,
  formatApiError,
} from "@/lib/api";

const FILTERS = [
  { key: "", label: "All", icon: Inbox },
  { key: "franchise", label: "Franchise", icon: Store },
  { key: "booking", label: "Bookings", icon: CalendarCheck },
];

function LoginScreen({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await adminLogin(email, password);
      setToken(data.access_token);
      onSuccess(data.user);
      toast.success("Welcome back, Admin.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#050505] px-6">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="mb-8 flex items-center gap-3">
          <img src="/carfix-logo.jpeg" alt="CARFIX AUTO" className="h-12 w-12 rounded-full" />
          <div>
            <div className="font-display text-xl font-black uppercase tracking-tight text-white">
              Carfix<span className="text-[var(--volt)]"> Auto</span>
            </div>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40">
              Admin Console
            </div>
          </div>
        </div>

        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="rounded-2xl border border-white/10 bg-[#0e0e0e] p-8"
        >
          <h1 className="font-display text-2xl font-bold text-white">Sign in</h1>
          <p className="mt-2 font-mono text-xs text-white/40">
            Enter your admin credentials to view enquiries.
          </p>

          <label className="mt-8 block font-mono text-[11px] uppercase tracking-wider text-white/50">
            Email
          </label>
          <input
            data-testid="admin-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 w-full rounded-lg border border-white/15 bg-transparent px-4 py-3 font-mono text-sm text-white focus:border-[var(--volt)] focus:outline-none"
            placeholder="admin@carfixauto.in"
          />

          <label className="mt-5 block font-mono text-[11px] uppercase tracking-wider text-white/50">
            Password
          </label>
          <input
            data-testid="admin-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-2 w-full rounded-lg border border-white/15 bg-transparent px-4 py-3 font-mono text-sm text-white focus:border-[var(--volt)] focus:outline-none"
            placeholder="••••••••"
          />

          <button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-[var(--volt)] px-6 py-3.5 font-mono text-xs font-bold uppercase tracking-[0.2em] text-black disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="rounded-xl border border-white/10 bg-[#0e0e0e] p-6">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[11px] uppercase tracking-wider text-white/50">
          {label}
        </span>
        <Icon className="h-4 w-4 text-[var(--volt)]" />
      </div>
      <div className="mt-3 font-display text-4xl font-black text-white">{value}</div>
    </div>
  );
}

function Dashboard({ user, onLogout }) {
  const [filter, setFilter] = useState("");
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({ total: 0, franchise: 0, booking: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, s] = await Promise.all([fetchLeads(filter), fetchStats()]);
      setLeads(l);
      setStats(s);
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Failed to load enquiries.");
    } finally {
      setLoading(false);
    }
  }, [filter, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const fmtDate = (iso) =>
    new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="sticky top-0 z-10 border-b border-white/10 bg-black/70 backdrop-blur-xl">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4">
          <div className="flex items-center gap-3">
            <img src="/carfix-logo.jpeg" alt="CARFIX AUTO" className="h-9 w-9 rounded-full" />
            <div>
              <div className="font-display text-sm font-black uppercase tracking-tight">
                Carfix Auto — Admin
              </div>
              <div className="font-mono text-[10px] text-white/40">{user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="refresh-btn"
              onClick={load}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-white/70 transition-colors hover:text-[var(--volt)]"
              aria-label="Refresh"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              data-testid="logout-btn"
              onClick={onLogout}
              className="flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono text-xs uppercase tracking-wider text-white/70 transition-colors hover:border-[var(--blaze)] hover:text-[var(--blaze)]"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <h1 className="font-display text-3xl font-bold uppercase tracking-tight md:text-4xl">
          Enquiries
        </h1>

        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <StatCard label="Total" value={stats.total} icon={Inbox} />
          <StatCard label="Franchise" value={stats.franchise} icon={Store} />
          <StatCard label="Bookings" value={stats.booking} icon={CalendarCheck} />
        </div>

        <div className="mt-10 flex gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key || "all"}
              data-testid={`filter-${f.label.toLowerCase()}`}
              onClick={() => setFilter(f.key)}
              className={`flex items-center gap-2 rounded-full px-5 py-2.5 font-mono text-xs uppercase tracking-wider transition-colors ${
                filter === f.key
                  ? "bg-[var(--volt)] text-black"
                  : "border border-white/15 text-white/60 hover:text-white"
              }`}
            >
              <f.icon className="h-3.5 w-3.5" />
              {f.label}
            </button>
          ))}
        </div>

        <div className="mt-6" data-testid="leads-list">
          {loading ? (
            <div className="flex items-center justify-center py-24 text-white/40">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          ) : leads.length === 0 ? (
            <div className="rounded-xl border border-dashed border-white/15 py-24 text-center font-mono text-sm text-white/40">
              No enquiries yet.
            </div>
          ) : (
            <div className="space-y-3">
              {leads.map((lead) => (
                <motion.div
                  key={lead.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="lead-card"
                  className="rounded-xl border border-white/10 bg-[#0e0e0e] p-5"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-3">
                        <span className="font-display text-lg font-bold">{lead.name}</span>
                        <span
                          className={`rounded-full px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider ${
                            lead.kind === "franchise"
                              ? "bg-[var(--volt)]/15 text-[var(--volt)]"
                              : "bg-white/10 text-white/70"
                          }`}
                        >
                          {lead.kind}
                        </span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 font-mono text-xs text-white/60">
                        <a href={`tel:${lead.phone}`} className="flex items-center gap-1.5 hover:text-[var(--volt)]">
                          <Phone className="h-3 w-3" /> {lead.phone}
                        </a>
                        {lead.email && (
                          <a href={`mailto:${lead.email}`} className="flex items-center gap-1.5 hover:text-[var(--volt)]">
                            <Mail className="h-3 w-3" /> {lead.email}
                          </a>
                        )}
                        {lead.city && (
                          <span className="flex items-center gap-1.5">
                            <MapPin className="h-3 w-3" /> {lead.city}
                          </span>
                        )}
                        {lead.service && <span>Service: {lead.service}</span>}
                      </div>
                      {lead.message && (
                        <p className="mt-3 max-w-2xl text-sm text-white/70">{lead.message}</p>
                      )}
                    </div>
                    <span className="font-mono text-[11px] text-white/40">
                      {fmtDate(lead.created_at)}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const init = async () => {
      if (!getToken()) {
        setChecking(false);
        return;
      }
      try {
        const me = await fetchMe();
        setUser(me);
      } catch {
        clearToken();
      } finally {
        setChecking(false);
      }
    };
    init();
  }, []);

  const logout = () => {
    clearToken();
    setUser(null);
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#050505]">
        <Loader2 className="h-6 w-6 animate-spin text-[var(--volt)]" />
      </div>
    );
  }

  return (
    <>
      <Toaster position="top-center" theme="dark" toastOptions={{ style: { fontFamily: "JetBrains Mono, monospace" } }} />
      {user ? (
        <Dashboard user={user} onLogout={logout} />
      ) : (
        <LoginScreen onSuccess={setUser} />
      )}
    </>
  );
}
